import axios from 'axios';
import { useToast } from 'vue-toastification';

let inFlight = false;

/**
 * Anthem inbound SSO (IdP-initiated).
 *
 * Opens a new browser tab, asks our API for a freshly-signed SAML assertion
 * (the Bearer token is attached automatically by the axios interceptor in
 * `services/axiosConfig.ts`), then writes the returned auto-submit HTML form
 * into that tab. The form immediately POSTs the assertion to Anthem's ACS URL,
 * which logs the participant into Anthem's portal.
 *
 * The tab is opened synchronously inside the click handler so browser pop-up
 * blockers treat it as user-initiated.
 */
export async function openAnthemSso(): Promise<void> {
    if (inFlight) {
        return;
    }
    inFlight = true;

    const toast = useToast();
    const ssoWindow = window.open('', '_blank');

    if (ssoWindow) {
        ssoWindow.document.write(
            '<p style="font-family:sans-serif;padding:24px">Connecting to Anthem…</p>'
        );
    }

    try {
        // GET returns an HTML auto-submit form (not JSON).
        const { data } = await axios.get('/api/sso/anthem/login', { responseType: 'text' });

        if (ssoWindow) {
            ssoWindow.document.open();
            ssoWindow.document.write(data); // auto-submits to Anthem's ACS
            ssoWindow.document.close();
        } else {
            toast.error('Please allow pop-ups to sign in to Anthem.');
        }
    } catch (error) {
        ssoWindow?.close();
        toast.error('Unable to connect to Anthem. Please try again.');
    } finally {
        inFlight = false;
    }
}

// Make `$anthemSsoLogin` known to TypeScript inside `<template>` blocks.
declare module 'vue' {
    interface ComponentCustomProperties {
        $anthemSsoLogin: typeof openAnthemSso;
    }
}
